import type { LucideIcon } from "lucide-react";
import { Landmark, Lock, Stamp } from "lucide-react";
import { cn } from "@bindery/ui/lib/utils";
import { CheckoutShell } from "@/components/checkout-shell";
import { MICRO_CAPS } from "@/components/type";
import { displayCheckoutHost } from "@/lib/display-host";

/**
 * `/` — the only page here anyone opens on purpose, which is why it gets a
 * little more room than the two interstitials do. Someone reaching it has
 * usually seen this host on a card statement or in a browser bar and
 * wants to know what it is; the page's whole job is to be legible and boring
 * in the reassuring sense.
 *
 * Ported loosely rather than literally from the monorepo's checkout landing
 * page: the three claims and their wording carry over intact, the layout does
 * not. The source used a shadcn Card, which reads as a component floating on
 * a page; everything else on this surface is ruled paper, so the three points
 * are hairline-divided entries instead.
 */

interface Point {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly body: string;
}

const POINTS: readonly Point[] = [
  {
    icon: Lock,
    title: "Card data never touches Sara servers",
    body: "Payment details are entered directly with Stripe, our payment processor. Sara’s Books never sees or stores your card number.",
  },
  {
    icon: Stamp,
    title: "Cryptographically signed receipts",
    body: "Every successful payment produces a signed receipt that the requesting app verifies independently before granting access.",
  },
  {
    icon: Landmark,
    title: "One checkout for the ecosystem",
    body: "A single, shared payment flow used across Sara’s Books products, so the experience is the same wherever you buy.",
  },
];

export function AboutPage() {
  return (
    <CheckoutShell title="Checkout · Sara's Books">
      <div className="flex flex-col gap-9">
        <header className="flex flex-col gap-4">
          {/* The ledger vocabulary again, used here as an eyebrow: a
              micro-caps key with the leader running out to the measure. It is
              the same device as a row on the return page, which is what makes
              three otherwise unrelated pages read as one document. */}
          <div className="flex items-baseline gap-3">
            <span className={cn(MICRO_CAPS, "text-muted-foreground shrink-0")}>
              About this service
            </span>
            <span aria-hidden className="text-rule leader h-[1.5px] flex-1" />
          </div>

          <h1 className="font-serif text-[1.75rem] leading-[1.15] tracking-[-0.015em]">
            The Sara&rsquo;s Books checkout
          </h1>

          <p className="text-muted-foreground text-sm leading-relaxed">
            <span className="text-foreground font-mono text-[0.8125rem]">
              {displayCheckoutHost(window.location.hostname)}
            </span>{" "}
            is the payments service for the Sara&rsquo;s Books ecosystem. You
            reach it when a Sara&rsquo;s Books product sends you here to
            complete a purchase.
          </p>
        </header>

        <dl className="divide-rule border-rule flex flex-col divide-y border-y">
          {POINTS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex items-start gap-4 py-5">
              {/* A thin-ruled square rather than a filled circle: a stamp on a
                  document, not a chip on a dashboard. */}
              <span
                aria-hidden
                className="border-rule text-muted-foreground mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-sm border"
              >
                <Icon className="size-4" />
              </span>
              <div className="flex min-w-0 flex-col gap-1.5">
                <dt className="text-sm leading-snug font-medium">{title}</dt>
                <dd className="text-muted-foreground text-sm leading-relaxed">
                  {body}
                </dd>
              </div>
            </div>
          ))}
        </dl>

        <p className="text-muted-foreground text-center text-sm">
          Looking for the main site?{" "}
          <a
            href="https://sara.ai"
            className="text-foreground font-medium underline underline-offset-4"
          >
            sara.ai
          </a>
        </p>
      </div>
    </CheckoutShell>
  );
}
